/* eslint-disable no-param-reassign */
import Vue from 'vue'
import { reactive, computed } from '@vue/composition-api'
import AggregationApi from '@/api/v2/eod-closing/Aggregation'

// クロージング画面で起動した「完了する」（bulk_complete）の進捗を、画面遷移を
// またいで保持・ポーリングする singleton。トーストの表示と、各クロージング画面の
// 進捗チェックリスト UI から参照される。
//
// モジュールレベルの reactive で 1 つだけ持つ。
//
// state.runningByType: {
//   EodClosingUserAggregation: {
//     jobId, status, total, succeeded, failed, skipped, results, dismissed
//   },
//   ...
// }

const state = reactive({
  runningByType: {
    EodClosingUserAggregation: null,
    EodClosingTableAggregation: null,
    EodClosingClubAggregation: null,
  },
  // 「完了通知」が一度ユーザーに表示されたあと × で閉じられたかどうか
  dismissedByType: {
    EodClosingUserAggregation: false,
    EodClosingTableAggregation: false,
    EodClosingClubAggregation: false,
  },
})

// 同時走行中の型がいくつあっても、ポーリングは 1 つだけ走らせる。
let pollTimer = null
const POLL_INTERVAL_MS = 3000

const POLLABLE_STATUSES = ['in_progress']

const isCompleted = entry => entry?.status === 'completed'
const isPollable = entry => POLLABLE_STATUSES.includes(entry?.status)

const anyPollable = () => Object.values(state.runningByType).some(isPollable)

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const fetchAndApply = async (type, jobId) => {
  try {
    const res = await AggregationApi.getBulkCompleteStatus(jobId)
    const payload = res.data?.data ?? res.data ?? {}
    const existing = state.runningByType[type]
    Vue.set(state.runningByType, type, {
      jobId,
      date: existing?.date ?? null,
      // 実行対象 ID（起動時の expectedOrder）はポーリングのレスポンスに含まれない
      // ため、起動時に保持したものを引き継ぐ。進捗カードの対象絞り込みに使う。
      aggregationIds: existing?.aggregationIds ?? [],
      status: payload.status,
      total: payload.total ?? 0,
      succeeded: payload.succeeded ?? 0,
      failed: payload.failed ?? 0,
      skipped: payload.skipped ?? 0,
      results: payload.results || [],
    })
  } catch (error) {
    // 取得失敗時はこのループの該当 type のみクリア（他に影響させない）。
    Vue.set(state.runningByType, type, null)
    // eslint-disable-next-line no-console
    console.error('bulk_complete status の取得に失敗', error)
  }
}

const tick = async () => {
  const pollables = Object.entries(state.runningByType).filter(([, entry]) => isPollable(entry))
  await Promise.all(pollables.map(([type, entry]) => fetchAndApply(type, entry.jobId)))
  if (!anyPollable()) stopPolling()
}

const startPolling = () => {
  if (pollTimer) return
  pollTimer = setInterval(tick, POLL_INTERVAL_MS)
}

export default function useGlobalBulkCompletion() {
  // クロージング画面の「完了する」押下時に呼ぶ。
  // res: { jobId, aggregationCount, expectedOrder, status } を返してくる。
  // aggregationIds（配列）を渡すとその集計表だけを実行する（完了済みスキップ）。
  // 未指定なら全表示対象を実行する（force-all）。
  const startBulkComplete = async ({
    type, date, aggregationIds = null, onError,
  }) => {
    try {
      const res = await AggregationApi.bulkComplete(type, date, aggregationIds)
      if (res.status !== 202) return null
      const jobId = res.data?.jobId ?? res.data?.job_id
      const expectedOrder = res.data?.expectedOrder ?? res.data?.expected_order ?? []
      Vue.set(state.runningByType, type, {
        jobId,
        date,
        aggregationIds: expectedOrder,
        status: res.data?.status ?? 'in_progress',
        total: res.data?.aggregationCount ?? res.data?.aggregation_count ?? 0,
        succeeded: 0,
        failed: 0,
        skipped: 0,
        results: [],
      })
      Vue.set(state.dismissedByType, type, false)
      startPolling()

      return { jobId, status: res.data?.status }
    } catch (error) {
      if (typeof onError === 'function') onError(error)

      return null
    }
  }

  const dismissCompletion = type => {
    Vue.set(state.dismissedByType, type, true)
    // 完了済みなら表示も消す
    if (isCompleted(state.runningByType[type])) {
      Vue.set(state.runningByType, type, null)
    }
  }

  const dismissAll = () => {
    Object.keys(state.runningByType).forEach(dismissCompletion)
  }

  // 完了済みの通知だけを閉じる（実行中は残す）。
  // トーストをクリックしてクロージングに遷移したときに使う。実行中の通知は
  // 閉じずに残し、他画面に移動しても集計が終わるまで再表示されるようにする。
  const dismissCompleted = () => {
    Object.entries(state.runningByType).forEach(([type, entry]) => {
      if (isCompleted(entry)) dismissCompletion(type)
    })
  }

  // 通知トーストに表示する「いま動いている／完了して未確認の」型のリスト
  const visibleNotifications = computed(() => (
    Object.entries(state.runningByType)
      .filter(([type, entry]) => entry && !state.dismissedByType[type])
      .map(([type, entry]) => ({ type, ...entry }))
  ))

  const isAnyRunning = computed(() => (
    Object.values(state.runningByType).some(isPollable)
  ))

  const getByType = type => state.runningByType[type]

  return {
    runningByType: state.runningByType,
    startBulkComplete,
    dismissCompletion,
    dismissAll,
    dismissCompleted,
    visibleNotifications,
    isAnyRunning,
    getByType,
  }
}
